import type { MotifName } from '@/content/missions'

// Motifs are drawn once, as stroked geometry on a 24-unit grid. The simple and
// line emblems render the geometry as vectors; the pixel emblem rasterizes the
// same geometry, so every style of a mark stays recognizably the same drawing.

type Point = [number, number]
export type Shape =
  | { kind: 'poly'; points: Point[]; closed?: boolean }
  | { kind: 'circle'; cx: number; cy: number; r: number }

const poly = (points: Point[], closed = false): Shape => ({ kind: 'poly', points, closed })
const line = (x1: number, y1: number, x2: number, y2: number): Shape =>
  poly([
    [x1, y1],
    [x2, y2],
  ])
const circle = (cx: number, cy: number, r: number): Shape => ({ kind: 'circle', cx, cy, r })
const rect = (x1: number, y1: number, x2: number, y2: number): Shape =>
  poly(
    [
      [x1, y1],
      [x2, y1],
      [x2, y2],
      [x1, y2],
    ],
    true,
  )

function ellipse(cx: number, cy: number, rx: number, ry: number, degrees = 0, steps = 36): Shape {
  const t = (degrees * Math.PI) / 180
  return poly(
    Array.from({ length: steps }, (_, i): Point => {
      const a = (i / steps) * Math.PI * 2
      const x = rx * Math.cos(a)
      const y = ry * Math.sin(a)
      return [cx + x * Math.cos(t) - y * Math.sin(t), cy + x * Math.sin(t) + y * Math.cos(t)]
    }),
    true,
  )
}

function arc(cx: number, cy: number, r: number, from: number, to: number, steps = 18): Shape {
  return poly(
    Array.from({ length: steps + 1 }, (_, i): Point => {
      const a = ((from + ((to - from) * i) / steps) * Math.PI) / 180
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
    }),
  )
}

function star(cx: number, cy: number, outer: number, inner = outer * 0.42): Shape {
  return poly(
    Array.from({ length: 10 }, (_, i): Point => {
      const a = (-90 + i * 36) * (Math.PI / 180)
      const r = i % 2 === 0 ? outer : inner
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
    }),
    true,
  )
}

function spiralArm(rotation: number): Shape {
  const t0 = (rotation * Math.PI) / 180
  return poly(
    Array.from({ length: 16 }, (_, i): Point => {
      const t = (i / 15) * Math.PI * 1.1
      const r = 1.2 + i * 0.52
      return [12 + r * Math.cos(t + t0), 12 + r * Math.sin(t + t0)]
    }),
  )
}

function wave(y: number): Shape {
  return poly(
    Array.from({ length: 25 }, (_, i): Point => [
      2 + (i * 20) / 24,
      y + Math.sin((i / 24) * Math.PI * 3) * 2,
    ]),
  )
}

export const motifs: Record<MotifName, Shape[]> = {
  star: [star(12, 12.6, 9)],
  eagle: [
    poly(
      [
        [2.5, 8],
        [7.5, 10.5],
        [12, 6.5],
        [16.5, 10.5],
        [21.5, 8],
        [18.5, 14.5],
        [12, 12.5],
        [5.5, 14.5],
      ],
      true,
    ),
    circle(12, 4.6, 1.6),
    line(12, 12.5, 9.5, 19.5),
    line(12, 12.5, 12, 20.5),
    line(12, 12.5, 14.5, 19.5),
  ],
  atom: [ellipse(12, 12, 9.5, 3.6, 0), ellipse(12, 12, 9.5, 3.6, 60), ellipse(12, 12, 9.5, 3.6, 120), circle(12, 12, 1.4)],
  bison: [
    poly(
      [
        [3, 15],
        [3.5, 10],
        [6.5, 6.5],
        [11, 6.5],
        [13, 8.5],
        [17.5, 8.5],
        [20.5, 11],
        [21, 15],
        [19.5, 15],
        [19.5, 18.5],
        [17.5, 18.5],
        [17.5, 15],
        [9, 15],
        [9, 18.5],
        [7, 18.5],
        [7, 15],
      ],
      true,
    ),
    line(5, 8, 3.5, 5.5),
  ],
  wheat: [
    line(12, 21, 12, 4.5),
    ellipse(9.6, 8, 1.3, 2.6, -30),
    ellipse(14.4, 8, 1.3, 2.6, 30),
    ellipse(9.6, 12.2, 1.3, 2.6, -30),
    ellipse(14.4, 12.2, 1.3, 2.6, 30),
    ellipse(9.6, 16.4, 1.3, 2.6, -30),
    ellipse(14.4, 16.4, 1.3, 2.6, 30),
  ],
  lighthouse: [
    poly(
      [
        [9, 21],
        [10, 9],
        [14, 9],
        [15, 21],
      ],
      true,
    ),
    rect(9.5, 5.5, 14.5, 9),
    poly([
      [9, 5.5],
      [12, 2.8],
      [15, 5.5],
    ]),
    line(15.5, 6.6, 21.5, 4.2),
    line(15.5, 8, 21.5, 10.4),
    line(5.5, 21, 18.5, 21),
  ],
  scales: [
    line(12, 4, 12, 20),
    line(8, 20, 16, 20),
    line(4.5, 7, 19.5, 7),
    poly([
      [2.5, 13.5],
      [5, 7],
      [7.5, 13.5],
    ]),
    arc(5, 13.5, 2.5, 0, 180),
    poly([
      [16.5, 13.5],
      [19, 7],
      [21.5, 13.5],
    ]),
    arc(19, 13.5, 2.5, 0, 180),
  ],
  key: [circle(7.5, 12, 4), line(11.5, 12, 21.5, 12), line(18, 12, 18, 15.5), line(21.5, 12, 21.5, 15.5)],
  shield: [
    poly(
      [
        [12, 2.8],
        [19.5, 5.8],
        [19, 13],
        [15.8, 18],
        [12, 21.2],
        [8.2, 18],
        [5, 13],
        [4.5, 5.8],
      ],
      true,
    ),
    line(12, 6, 12, 18),
    line(7.5, 10.5, 16.5, 10.5),
  ],
  globe: [circle(12, 12, 9), ellipse(12, 12, 4, 9), line(3, 12, 21, 12), line(4.5, 7.5, 19.5, 7.5), line(4.5, 16.5, 19.5, 16.5)],
  house: [
    poly([
      [3.5, 11.5],
      [12, 4],
      [20.5, 11.5],
    ]),
    poly([
      [6, 10],
      [6, 20.5],
      [18, 20.5],
      [18, 10],
    ]),
    rect(10.3, 14, 13.7, 20.5),
  ],
  oak: [line(12, 21, 12, 13.5), circle(12, 8.5, 4.5), circle(7.6, 11.5, 3.4), circle(16.4, 11.5, 3.4), line(9, 21, 15, 21)],
  sun: [
    circle(12, 12, 4.2),
    ...Array.from({ length: 8 }, (_, i) => {
      const a = (i * 45 * Math.PI) / 180
      return line(12 + 6.5 * Math.cos(a), 12 + 6.5 * Math.sin(a), 12 + 9.5 * Math.cos(a), 12 + 9.5 * Math.sin(a))
    }),
  ],
  swirl: [spiralArm(0), spiralArm(120), spiralArm(240), circle(12, 12, 10)],
  orbit: [circle(12, 12, 5.5), ellipse(12, 12, 10.5, 3.4, -24), star(18.5, 5.5, 2.4)],
  flower: [ellipse(12, 7.2, 2.8, 4.6, 0), ellipse(7.8, 14.4, 2.8, 4.6, -120), ellipse(16.2, 14.4, 2.8, 4.6, 120), circle(12, 12, 1.6)],
  anvil: [
    poly(
      [
        [3.5, 7.5],
        [18.5, 7.5],
        [20.5, 10],
        [15, 11],
        [14, 14],
        [16.5, 17],
        [7.5, 17],
        [10, 14],
        [9, 11],
        [3.5, 10],
      ],
      true,
    ),
    line(5.5, 20.5, 18.5, 20.5),
  ],
  capitol: [
    line(3.5, 20.5, 20.5, 20.5),
    line(5, 13, 19, 13),
    line(7, 13, 7, 20.5),
    line(10, 13, 10, 20.5),
    line(14, 13, 14, 20.5),
    line(17, 13, 17, 20.5),
    arc(12, 13, 5, 180, 360),
    line(12, 8, 12, 3.5),
  ],
  column: [rect(6.5, 3.5, 17.5, 6), line(9, 6, 9, 18.5), line(12, 6, 12, 18.5), line(15, 6, 15, 18.5), rect(5.5, 18.5, 18.5, 21)],
  envelope: [
    rect(3, 6, 21, 18),
    poly([
      [3, 6],
      [12, 13],
      [21, 6],
    ]),
  ],
  book: [
    poly([
      [12, 7],
      [3.5, 4.8],
      [3.5, 18],
      [12, 20.2],
    ]),
    poly([
      [12, 7],
      [20.5, 4.8],
      [20.5, 18],
      [12, 20.2],
    ]),
    line(12, 7, 12, 20.2),
  ],
  torch: [
    poly(
      [
        [12, 2.8],
        [14.8, 7],
        [12, 10],
        [9.2, 7],
      ],
      true,
    ),
    poly(
      [
        [8, 10.5],
        [16, 10.5],
        [14, 13.5],
        [10, 13.5],
      ],
      true,
    ),
    line(11, 13.5, 11.6, 21),
    line(13, 13.5, 12.4, 21),
  ],
  wave: [wave(8), wave(13), wave(18)],
  mountain: [
    poly([
      [2, 19.5],
      [9, 7.5],
      [13, 13.5],
      [16, 9.5],
      [22, 19.5],
    ]),
    line(2, 19.5, 22, 19.5),
    poly([
      [7.2, 10.6],
      [9, 12],
      [10.8, 10.6],
    ]),
  ],
  building: [rect(5, 3.5, 19, 21), rect(8, 7, 10.5, 9.5), rect(13.5, 7, 16, 9.5), rect(8, 12, 10.5, 14.5), rect(13.5, 12, 16, 14.5), rect(10.5, 17, 13.5, 21)],
  chart: [
    poly([
      [3.5, 3.5],
      [3.5, 20.5],
      [20.5, 20.5],
    ]),
    rect(7, 13, 9.5, 20.5),
    rect(12, 9.5, 14.5, 20.5),
    rect(17, 6, 19.5, 20.5),
  ],
  hands: [
    poly([
      [2.5, 13],
      [7, 9],
      [11, 11],
      [13.5, 9.5],
      [17, 9],
      [21.5, 13],
    ]),
    poly([
      [7, 16.5],
      [11, 13],
      [14, 15.5],
      [17, 13],
    ]),
    star(12, 4.6, 2.6),
  ],
  bolt: [
    poly(
      [
        [13.5, 2.5],
        [5.5, 13.5],
        [11, 13.5],
        [10, 21.5],
        [18.5, 10],
        [13, 10],
      ],
      true,
    ),
  ],
  anchor: [
    circle(12, 4.6, 1.8),
    line(12, 6.4, 12, 20.5),
    line(7.5, 9, 16.5, 9),
    arc(12, 13.5, 7, 20, 160),
    poly([
      [3.2, 14.2],
      [5.4, 16.6],
      [6.2, 13.4],
    ]),
    poly([
      [20.8, 14.2],
      [18.6, 16.6],
      [17.8, 13.4],
    ]),
  ],
  plane: [
    poly(
      [
        [12, 2.5],
        [13.4, 5],
        [13.4, 10],
        [21.5, 14.5],
        [21.5, 16],
        [13.4, 13.6],
        [13.2, 18.4],
        [15.8, 20.4],
        [15.8, 21.5],
        [12, 20.5],
        [8.2, 21.5],
        [8.2, 20.4],
        [10.8, 18.4],
        [10.6, 13.6],
        [2.5, 16],
        [2.5, 14.5],
        [10.6, 10],
        [10.6, 5],
      ],
      true,
    ),
  ],
  dove: [
    poly([
      [3, 13],
      [8, 11.5],
      [11.5, 13.5],
      [15.5, 12.5],
      [19, 9],
      [21, 9.5],
      [19.5, 11.5],
      [17.5, 15.5],
      [12.5, 17.5],
      [7.5, 16.5],
      [3, 13],
    ]),
    poly([
      [9.5, 12],
      [8, 5.5],
      [13.5, 9],
      [15.5, 12.5],
    ]),
    line(19, 9, 21.8, 7.8),
  ],
  scroll: [
    rect(6, 5, 18, 19),
    arc(6, 7, 2, 90, 270),
    arc(18, 17, 2, -90, 90),
    line(9, 9.5, 15, 9.5),
    line(9, 12.5, 15, 12.5),
    line(9, 15.5, 13, 15.5),
  ],
  pine: [
    poly(
      [
        [12, 2.5],
        [16.5, 9],
        [14.2, 9],
        [18.5, 14.5],
        [15.5, 14.5],
        [20, 19],
        [4, 19],
        [8.5, 14.5],
        [5.5, 14.5],
        [9.8, 9],
        [7.5, 9],
      ],
      true,
    ),
    line(12, 19, 12, 21.5),
  ],
  hammer: [
    line(5, 19.5, 17.5, 7),
    poly(
      [
        [14.5, 4],
        [20.5, 10],
        [18, 12.5],
        [12, 6.5],
      ],
      true,
    ),
    line(19, 19.5, 6.5, 7),
    poly([
      [4, 9.5],
      [6.5, 7],
      [9, 4.5],
    ]),
  ],
  delta: [
    poly(
      [
        [12, 2.5],
        [20.5, 21],
        [12, 16.5],
        [3.5, 21],
      ],
      true,
    ),
    star(12, 11.5, 2.6),
  ],
  road: [
    poly([
      [9.5, 3],
      [3, 21],
    ]),
    poly([
      [14.5, 3],
      [21, 21],
    ]),
    line(12, 3.5, 12, 6.5),
    line(12, 9.5, 12, 13),
    line(12, 16, 12, 20.5),
  ],
  car: [
    poly(
      [
        [3, 15.5],
        [3, 12.5],
        [6, 11.5],
        [8.5, 7.5],
        [15.5, 7.5],
        [18, 11.5],
        [21, 12.5],
        [21, 15.5],
      ],
      true,
    ),
    circle(7.5, 16.5, 2),
    circle(16.5, 16.5, 2),
    line(9.5, 11.5, 14.5, 11.5),
  ],
  helix: [
    poly(Array.from({ length: 25 }, (_, i): Point => [12 + Math.sin((i / 24) * Math.PI * 3) * 5, 2.5 + (i * 19) / 24])),
    poly(Array.from({ length: 25 }, (_, i): Point => [12 - Math.sin((i / 24) * Math.PI * 3) * 5, 2.5 + (i * 19) / 24])),
    line(8, 7.2, 16, 7.2),
    line(8, 13.5, 16, 13.5),
    line(8, 19.8, 16, 19.8),
  ],
  flask: [
    poly([
      [10, 3],
      [10, 9.5],
      [4.5, 19.5],
      [19.5, 19.5],
      [14, 9.5],
      [14, 3],
    ]),
    line(8.5, 3, 15.5, 3),
    line(7.2, 15, 16.8, 15),
  ],
  bulb: [
    arc(12, 10, 6.5, 145, 395),
    poly([
      [6.7, 13.8],
      [9, 17],
      [15, 17],
      [17.3, 13.8],
    ]),
    line(9.5, 19, 14.5, 19),
    line(10.5, 21, 13.5, 21),
    poly([
      [10, 12],
      [12, 9],
      [14, 12],
    ]),
  ],
}

export function shapePath(shape: Shape): string {
  if (shape.kind === 'circle') {
    const { cx, cy, r } = shape
    return `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0`
  }
  const [first, ...rest] = shape.points
  return `M${first.map(round).join(' ')}${rest.map((p) => `L${p.map(round).join(' ')}`).join('')}${shape.closed ? 'Z' : ''}`
}

const round = (value: number) => Math.round(value * 100) / 100

function segmentDistance([px, py]: Point, [ax, ay]: Point, [bx, by]: Point) {
  const dx = bx - ax
  const dy = by - ay
  const lengthSquared = dx * dx + dy * dy || 1
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSquared))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

function shapeDistance(point: Point, shape: Shape) {
  if (shape.kind === 'circle')
    return Math.abs(Math.hypot(point[0] - shape.cx, point[1] - shape.cy) - shape.r)
  const points = shape.closed ? [...shape.points, shape.points[0]] : shape.points
  let best = Infinity
  for (let i = 0; i < points.length - 1; i += 1)
    best = Math.min(best, segmentDistance(point, points[i], points[i + 1]))
  return best
}

/** Rasterizes a motif into a square grid of on/off cells. */
export function rasterize(shapes: Shape[], size = 16, weight = 0.95): boolean[][] {
  const cell = 24 / size
  return Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_, col) => {
      const point: Point = [(col + 0.5) * cell, (row + 0.5) * cell]
      return shapes.some((shape) => shapeDistance(point, shape) <= weight * (cell / 1.5))
    }),
  )
}
